import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Circle, Loader2, RefreshCw, X, ZapOff } from "lucide-react";
import { useLanguage } from "../i18n";

const MAX_SECONDS = 5;
const MIME_CANDIDATES = ["video/mp4", "video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];

// Video selfie guidato (prova liveness): 5 secondi con fotocamera frontale,
// il cliente guarda avanti, gira la testa e legge ad alta voce il codice mostrato
// (anti-replay: il codice è casuale per ogni pratica). Anteprima e riprova prima dell'invio.
export function VideoCapture({ title, challenge, onCapture, onClose }) {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(false);
  const [noAudio, setNoAudio] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null); // {url, file}

  const mimeType = (typeof window !== "undefined" && window.MediaRecorder)
    ? MIME_CANDIDATES.find((m) => window.MediaRecorder.isTypeSupported(m)) || "" : "";
  const ext = mimeType.includes("mp4") ? "mp4" : "webm";

  useEffect(() => {
    let alive = true;
    const constraints = { video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 960 } }, audio: true };
    // microfono negato → si registra comunque senza audio (il controllo vocale
    // risulterà "da verificare" e l'admin deciderà: mai bloccare il cliente)
    navigator.mediaDevices?.getUserMedia(constraints)
      .catch(() => { setNoAudio(true); return navigator.mediaDevices.getUserMedia({ ...constraints, audio: false }); })
      .then((stream) => {
        if (!alive) { stream.getTracks().forEach((track) => track.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
        setReady(true);
      }).catch(() => setError(true));
    return () => {
      alive = false;
      clearTimeout(tickRef.current);
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  const startRecording = () => {
    if (!streamRef.current || !mimeType) { setError(true); return; }
    chunksRef.current = [];
    const recorder = new MediaRecorder(streamRef.current, { mimeType, videoBitsPerSecond: 2_000_000 });
    recorderRef.current = recorder;
    recorder.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const file = new File([blob], `selfie-video-${Date.now()}.${ext}`, { type: mimeType });
      setPreview({ url: URL.createObjectURL(blob), file });
      setRecording(false);
    };
    recorder.start(250);
    setElapsed(0);
    setRecording(true);
    const startedAt = Date.now();
    const tick = () => {
      const secs = (Date.now() - startedAt) / 1000;
      setElapsed(secs);
      if (secs >= MAX_SECONDS) {
        if (recorder.state !== "inactive") recorder.stop();
        return;
      }
      tickRef.current = setTimeout(tick, 200);
    };
    tick();
  };

  const retake = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setElapsed(0);
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <div data-testid="video-capture-modal" className="fixed inset-0 z-[400] bg-[#0F172A] text-white flex flex-col">
      <div className="flex items-center justify-between px-5 py-4">
        <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.22em] text-slate-300">{title}</div>
        <button type="button" data-testid="video-capture-close" onClick={onClose} aria-label={t("auto.close")}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {preview ? (
          <video ref={videoRef} data-testid="video-capture-preview" src={preview.url} controls autoPlay loop muted playsInline
            className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <video ref={videoRef} data-testid="video-capture-live" autoPlay playsInline muted
            className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-slate-950/20 pointer-events-none" aria-hidden="true" />
        {!preview && (
          <div data-testid="video-capture-frame" aria-hidden="true"
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-72 rounded-[50%] border-4 shadow-[0_0_0_999px_rgba(15,23,42,0.45)] transition-colors duration-300 ${
              recording ? "border-rose-500" : "border-[#FF6B35]"}`} />
        )}
        {challenge && !preview && (
          <div data-testid="video-challenge-code"
            className="absolute left-1/2 bottom-5 -translate-x-1/2 rounded-3xl bg-slate-950/70 backdrop-blur px-6 py-3 text-center">
            <div className="font-mono-t text-[9px] font-bold uppercase tracking-[0.2em] text-slate-300">
              {t("id.videoCode")}{noAudio ? ` · ${t("id.videoNoAudio")}` : ""}
            </div>
            <div className="mt-1 font-display font-black text-4xl tracking-[0.35em] text-white pl-[0.35em]">
              {challenge.split("").join(" ")}
            </div>
          </div>
        )}
        {recording && (
          <div data-testid="video-recording-indicator"
            className="absolute left-1/2 top-4 -translate-x-1/2 flex items-center gap-2 rounded-full bg-rose-600/90 px-4 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-[0.18em] text-white shadow-lg">
            <Circle className="w-2.5 h-2.5 fill-white animate-pulse" />
            {Math.max(0, Math.ceil(MAX_SECONDS - elapsed))}s
          </div>
        )}
        {!ready && !error && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>}
        {error && (
          <div data-testid="video-capture-error" className="absolute inset-x-6 top-1/3 rounded-3xl bg-white text-slate-800 p-5 text-center">
            <ZapOff className="w-8 h-8 mx-auto text-[#FF6B35]" />
            <p className="mt-3 text-sm">{t("id.videoUnsupported")}</p>
          </div>
        )}
      </div>
      <div className="px-6 pb-8 pt-4 text-center safe-area-bottom">
        <p className="text-sm text-slate-300">{t("id.stepVideo")}</p>
        {preview ? (
          <div className="mt-4 grid grid-cols-2 gap-3 max-w-sm mx-auto">
            <button type="button" data-testid="video-retake-button" onClick={retake}
              className="rounded-full border border-white/30 px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-white">
              <RefreshCw className="inline w-3.5 h-3.5 mr-1" /> {t("id.videoAgain")}
            </button>
            <button type="button" data-testid="video-use-button" onClick={() => onCapture(preview.file)}
              className="rounded-full bg-[#FF6B35] px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest text-white">
              <CheckCircle2 className="inline w-3.5 h-3.5 mr-1" /> {t("id.videoUse")}
            </button>
          </div>
        ) : (
          <button type="button" data-testid="video-record-button" onClick={startRecording} disabled={!ready || recording}
            className={`mt-4 w-20 h-20 rounded-full border-4 mx-auto flex items-center justify-center shadow-xl transition-colors disabled:opacity-50 ${
              recording ? "border-rose-400 bg-rose-600" : "border-white bg-[#FF6B35]"}`}>
            {recording ? <span className="w-6 h-6 rounded-sm bg-white" /> : <Circle className="w-8 h-8 fill-white text-white" />}
          </button>
        )}
      </div>
    </div>
  );
}
